import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import BannerSlot from "../components/BannerSlot";
import { ArrowRight, ArrowUpRight, Truck, Anchor, Plane, Building2, Bus, Users, MapPin, Search, Sparkles, Zap } from "lucide-react";

const SERVICE_ICONS = {
  "motorhome-removal": Truck,
  "rv-disposal": Truck,
  "camper-removal": Truck,
  "boat-removal": Anchor,
  "semi-trailer-removal": Truck,
  "bus-prevost-removal": Bus,
  "airplane-removal": Plane,
  "helicopter-removal": Plane,
  "real-property-removal": Building2,
  "squatter-removal": Users,
};

export default function Home() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({ total: 0, us: 0, ca: 0 });
  const [featured, setFeatured] = useState([]);
  const [news, setNews] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data));
    api.get("/cities/stats").then(r => setStats(r.data));
    api.get("/cities/featured").then(r => setFeatured(r.data));
    api.get("/news").then(r => setNews(r.data.slice(0, 4)));
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/cities/search", { params: { q, limit: 6 } })
        .then(r => { if (!cancelled) setResults(r.data); })
        .catch((err) => console.error("City search failed:", err));
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <>
      {/* HERO */}
      <section className="federal-hero" data-testid="hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <span className="sticker"><Zap className="w-3.5 h-3.5" /> 24/7 · USA + Canada · Bonded</span>

              <h1 className="display-black text-5xl sm:text-6xl lg:text-[88px] mt-6 max-w-4xl">
                Got something <span className="hl-coral">huge</span> in the way?
                <br />
                <span className="hl-cobalt">We haul it off.</span>
              </h1>

              <p className="text-lg lg:text-xl text-[#4b5468] mt-7 max-w-2xl leading-relaxed">
                Motorhomes. Yachts. Prevost coaches. Mansions. Even derelict aircraft. Available is the friendly heavyweight of disposal — <span className="font-semibold text-[#0b1020]">{stats.total.toLocaleString()}+ cities</span> across North America, one number, zero excuses.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">
                <Link to="/quote" className="btn-signal" data-testid="hero-quote-btn">
                  Get a Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="tel:9184073395" className="btn-federal" data-testid="hero-call-btn">
                  Call 918-407-3395
                </a>
                <Link to="/services" className="btn-ghost" data-testid="hero-browse-services-btn">
                  Browse 10 Services
                </Link>
              </div>

              {/* City quick-find */}
              <div className="mt-10 max-w-xl">
                <label className="text-[12px] font-semibold text-[#4b5468]">Are we in your city?</label>
                <div className="mt-2 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4b5468]" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Start typing — Toronto, Houston, Calgary…"
                    className="input-federal pl-11"
                    data-testid="home-city-search"
                  />
                  {results.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-2 bg-white border border-[#e7e3da] rounded-2xl shadow-xl max-h-72 overflow-auto">
                      {results.map(c => (
                        <button
                          key={c.id}
                          onClick={() => { navigate(`/city/${c.slug}`); setQ(""); }}
                          className="w-full text-left px-4 py-3 hover:bg-[#fff7e6] flex items-center justify-between text-[#0b1020] border-b border-[#f5f1e8] last:border-b-0 first:rounded-t-2xl last:rounded-b-2xl"
                          data-testid={`search-result-${c.slug}`}
                        >
                          <span><span className="font-semibold">{c.name}</span> <span className="text-[#4b5468] text-sm">— {c.state}, {c.country}</span></span>
                          <ArrowUpRight className="w-4 h-4 text-[#f43f5e]" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Hero visual stack */}
            <div className="lg:col-span-5 relative hidden lg:block">
              <div className="absolute -top-6 -right-2 sticker floaty" style={{ background: "#fff", borderColor: "#0b1020" }}>
                <Sparkles className="w-3.5 h-3.5 text-[#f43f5e]" /> Independent & Owner-Operated
              </div>
              <div className="bento-card p-7 rotate-[2deg] shadow-[8px_8px_0_#0b1020]">
                <div className="text-[11px] text-[#f43f5e] font-semibold uppercase tracking-wider">Live</div>
                <div className="display-black text-3xl mt-2">A 53-ft yacht just left a marina in Tampa.</div>
                <div className="text-sm text-[#4b5468] mt-3">Marina liquidator booked us at 11pm. Trailer rolled by 6am.</div>
              </div>
              <div className="bento-card bento-card-dark p-7 mt-6 -rotate-[3deg] ml-10 shadow-[8px_8px_0_#f43f5e]">
                <div className="text-[11px] text-[#fde4b6] font-semibold uppercase tracking-wider">Big jobs</div>
                <div className="display-black text-3xl mt-2 text-white">100+ units? $250K+? You get a senior coordinator in 24h.</div>
              </div>
              <div className="bento-card p-5 mt-6 rotate-[1deg] mr-12 shadow-[6px_6px_0_#1d4ed8]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#d9f99d] rounded-xl flex items-center justify-center"><Plane className="w-5 h-5 text-[#0b1020]" /></div>
                  <div>
                    <div className="font-semibold text-[#0b1020]">Cessna 172 — Hangar 4</div>
                    <div className="text-xs text-[#4b5468]">Quoted in 4 hours, gone in 48</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-white border-y border-[#e7e3da]" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 grid-divider">
          {[
            { v: `${stats.total.toLocaleString()}+`, l: "Cities Served", c: "#1d4ed8" },
            { v: "10", l: "Removal Categories", c: "#f43f5e" },
            { v: "24/7", l: "National Dispatch", c: "#0b1020" },
            { v: "Bonded", l: "Insured Carriers", c: "#84cc16" },
          ].map((s) => (
            <div key={s.l} className="p-8 lg:p-10 text-center">
              <div className="display-black text-4xl lg:text-5xl" style={{ color: s.c }}>{s.v}</div>
              <div className="text-[12px] text-[#4b5468] mt-2 font-medium">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TOP BANNER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <BannerSlot placement="homepage_top" />
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="sec-num">01 — Categories</div>
              <h2 className="display-black text-4xl lg:text-6xl mt-4 text-[#0b1020] max-w-3xl">
                Ten divisions.<br />
                <span className="hl-coral">One phone call.</span>
              </h2>
            </div>
            <Link to="/services" className="btn-ghost shrink-0">All Services <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[s.slug] || Truck;
              const dark = i === 0 || i === 4 || i === 8;
              return (
                <Link
                  to={`/service/${s.slug}`}
                  key={s.slug}
                  className={`bento-card ${dark ? "bento-card-dark" : ""} p-7 lg:p-8 flex flex-col gap-4 min-h-[230px]`}
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${dark ? "bg-[#f43f5e]" : "bg-[#fff7e6]"}`}>
                      <Icon className={`w-6 h-6 ${dark ? "text-white" : "text-[#f43f5e]"}`} strokeWidth={2} />
                    </div>
                    <span className={`text-[11px] font-semibold ${dark ? "text-white/60" : "text-[#4b5468]"}`}>{String(i+1).padStart(2,"0")} / {String(services.length).padStart(2,"0")}</span>
                  </div>
                  <h3 className="display-black text-2xl leading-tight">{s.name}</h3>
                  <p className={`text-sm ${dark ? "text-white/80" : "text-[#4b5468]"} flex-1 leading-relaxed`}>{s.summary}</p>
                  <div className={`text-sm font-semibold ${dark ? "text-[#fde4b6]" : "text-[#f43f5e]"} flex items-center gap-2`}>
                    Dispatch now <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* LARGE JOBS CTA */}
      <section className="bg-[#0b1020] text-white py-20 lg:py-28 relative overflow-hidden" data-testid="large-jobs-cta">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#f43f5e]/20 blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#1d4ed8]/20 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <span className="sticker" style={{ background: "#f43f5e", color: "#fff", borderColor: "#fff", boxShadow: "3px 3px 0 #fff" }}>
              <Zap className="w-3.5 h-3.5" /> Priority Channel
            </span>
            <h2 className="display-black text-4xl lg:text-6xl mt-6">
              100+ units?<br />
              $250K+ budget?<br />
              <span className="text-[#fde4b6]">We want to talk — now.</span>
            </h2>
            <p className="text-white/75 mt-6 max-w-lg text-lg">
              Fleet operators, dealerships, marina liquidators, municipalities. Senior coordinator on the line within 24 hours.
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/large-jobs" className="btn-signal" data-testid="cta-large-jobs">Priority Intake</Link>
              <Link to="/consulting" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>Consulting</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["100+", "Units Threshold", "#f43f5e"],
              ["$250K", "Minimum Budget", "#fde4b6"],
              ["24 hr", "Coordinator Call", "#d9f99d"],
              ["Bonded", "Insured Carriers", "#fff"],
            ].map(([v, l, c]) => (
              <div key={l} className="bg-white/5 rounded-2xl border border-white/10 p-8 text-center backdrop-blur">
                <div className="display-black text-3xl lg:text-4xl" style={{ color: c }}>{v}</div>
                <div className="text-[12px] text-white/65 mt-2 font-medium">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MID BANNER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <BannerSlot placement="homepage_mid" />
      </section>

      {/* CITIES */}
      <section className="py-20 lg:py-28" data-testid="cities-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="sec-num">02 — Coverage</div>
              <h2 className="display-black text-4xl lg:text-6xl mt-4 text-[#0b1020]">
                {stats.total.toLocaleString()}+ cities.<br />
                <span className="hl-lime">{stats.us.toLocaleString()} U.S. · {stats.ca.toLocaleString()} Canada.</span>
              </h2>
            </div>
            <Link to="/cities" className="btn-ghost shrink-0">Browse All <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {featured.slice(0, 24).map(c => (
              <Link
                key={c.id}
                to={`/city/${c.slug}`}
                className="bento-card p-5 flex flex-col gap-2"
                data-testid={`city-tile-${c.slug}`}
              >
                <MapPin className="w-4 h-4 text-[#f43f5e]" />
                <div className="font-semibold text-sm text-[#0b1020] truncate">{c.name}</div>
                <div className="text-[11px] text-[#4b5468]">{c.state_code} · {c.country}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN US */}
      <section className="bg-[#fff7e6] py-20 lg:py-28" data-testid="join-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="sec-num">03 — Programs</div>
          <h2 className="display-black text-4xl lg:text-6xl mt-4 text-[#0b1020] max-w-3xl">
            Join the <span className="hl-coral">disposal network.</span>
          </h2>
          <p className="text-[#4b5468] mt-5 max-w-2xl text-lg">Three programs. Earn with us. Grow with us. Build your local presence inside the largest removal network in North America.</p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { title: "Power Sales Team", to: "/join/sales-team", desc: "$250K+ annual · 22% commission · Delaware C-Corp ownership. Work from your phone.", tag: "Tier 00 · Elite", accent: true },
              { title: "Preferred Haulers", to: "/join/haulers", desc: "Bonded carriers receive dispatch leads in their service area. Higher pay, vetted customers.", tag: "Tier 01" },
              { title: "Marketing Affiliates", to: "/join/affiliates", desc: "Influencers, fleet brokers, and channel partners earn recurring commission on every booking.", tag: "Tier 02" },
              { title: "Link Sharing", to: "/join/link-sharing", desc: "Websites and directories share branded links — earn per qualified click and conversion.", tag: "Tier 03" },
            ].map(c => (
              <Link
                key={c.title}
                to={c.to}
                className={`${c.accent ? "bento-card-dark" : "bento-card"} p-7 flex flex-col gap-4`}
                data-testid={`join-card-${c.to.split("/").pop()}`}
              >
                <div className={`text-[11px] font-semibold uppercase tracking-wider ${c.accent ? "text-[#fde4b6]" : "text-[#f43f5e]"}`}>{c.tag}</div>
                <h3 className={`display-black text-2xl ${c.accent ? "text-white" : "text-[#0b1020]"}`}>{c.title}</h3>
                <p className={`text-sm flex-1 leading-relaxed ${c.accent ? "text-white/80" : "text-[#4b5468]"}`}>{c.desc}</p>
                <div className={`text-sm font-semibold flex items-center gap-2 ${c.accent ? "text-[#fde4b6]" : "text-[#1d4ed8]"}`}>
                  Apply <ArrowUpRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="py-20 lg:py-28" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="sec-num">04 — Newsroom</div>
              <h2 className="display-black text-4xl lg:text-6xl mt-4 text-[#0b1020]">Industry <span className="hl-cobalt">pulse.</span></h2>
            </div>
            <Link to="/news" className="btn-ghost shrink-0">All News <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {news.map(n => (
              <div key={n.id} className="bento-card p-7" data-testid={`news-tile-${n.id}`}>
                <div className="text-[11px] text-[#4b5468] font-semibold uppercase tracking-wider">{n.region} · {n.published}</div>
                <h3 className="display-black text-2xl text-[#0b1020] mt-3 leading-tight">{n.title}</h3>
                <p className="text-sm text-[#4b5468] mt-3 leading-relaxed">{n.summary}</p>
                <div className="text-[12px] font-semibold text-[#f43f5e] mt-4">— {n.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#f43f5e] text-white py-20 lg:py-28 relative overflow-hidden" data-testid="final-cta">
        <div className="absolute -top-24 -left-24 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-center gap-10 justify-between relative">
          <div>
            <h2 className="display-black text-4xl lg:text-6xl max-w-2xl leading-[0.95]">
              Make this your start-up page. We&apos;re <span className="text-[#fde4b6]">available</span> — wherever you are.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/quote" className="btn-federal" style={{ background: "#fff", color: "#f43f5e", borderColor: "#fff" }}>Get a Quote →</Link>
            <Link to="/join" className="btn-ghost" style={{ color: "#fff", borderColor: "#fff" }}>Join Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
